import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import api, { isAuthenticated } from "../../utils/axiosConfig";

const CartContext = createContext();


export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resetCart = useCallback(() => {
    setItems([]);
    setTotalQuantity(0);
    setTotal(0);
  }, []);

  const calculateItemPrice = (product) => {
    if (!product || !product.price) return 0;

    if (product.promo && product.price.promoPrice) {
      return product.price.promoPrice;
    }

    return product.price.originalPrice || product.price;
  };

  const fetchCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/shop/cart`);

      const fetchedItems = res.data.items || [];

      setItems(fetchedItems);

      // ✅ Recalculer le total ici plutôt que de faire confiance à l’API
      const calculatedTotal = fetchedItems.reduce((sum, item) => {
        const price = calculateItemPrice(item.product);
        return sum + price * item.quantity;
      }, 0);

      setTotal(calculatedTotal);

      const totalQty = fetchedItems.reduce(
        (sum, item) => {
          // 🔍 FILTRER LES ARTICLES AVEC DES PRODUITS NULL
          if (!item.product || !item.product._id) {
            console.warn("⚠️ Article avec produit null ignoré dans le comptage:", item);
            return sum;
          }
          return sum + item.quantity;
        },
        0
      );
      setTotalQuantity(totalQty);
    } catch (err) {
      console.error("Erreur récupération panier :", err);
      if (err.response && err.response.status === 401) {
        resetCart();
      }
      setError("Erreur lors du chargement du panier");
    } finally {
      setLoading(false);
    }
  }, [resetCart]);

  const refreshCartCount = useCallback(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    // Vérifier si l'utilisateur a un token avant de charger le panier
    if (isAuthenticated()) {
      fetchCart();
    } else {
      // Pas de token = pas d'utilisateur connecté, pas besoin de charger le panier
      setLoading(false);
      setItems([]);
      setTotalQuantity(0);
      setTotal(0);
    }
  }, [fetchCart]);

  return (
    <CartContext.Provider
      value={{
        items,
        totalQuantity,
        total,
        loading,
        error,
        fetchCart,
        refreshCartCount,
        resetCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
